import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import adminApi from '../../services/adminApi';
import LoadingSpinner from '../../components/LoadingSpinner';
import { AdminModal, AdminField, AdminCheck, ConfirmModal } from '../../components/AdminModal';
import { apiErrorMessage } from '../../utils/apiError';
import {
  IoSearchOutline,
  IoCreateOutline,
  IoTrashOutline,
  IoSwapHorizontalOutline,
  IoRefreshOutline
} from 'react-icons/io5';
import AdminDataTable, { AdminPill } from '../../components/admin/AdminDataTable';
import BulkActionHost from '../../components/admin/BulkActionHost';
import {
  useBulkAction, useRowSelection, runBulk, summarizeBulk, useDebouncedValue, useLatestRequest, useFirstLoad
} from '../../components/admin/useAdminTable';
import './AdminReadersPage.css';

const ReaderEditModal = ({ reader, onClose, onSaved }) => {
  const initial = {
    name: reader.name ?? '',
    age: reader.age ?? '',
    gender: reader.gender ?? '',
    isPrimary: reader.isPrimary === true,
    isActive: reader.isActive !== false
  };
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const changes = {};
    if (form.name !== initial.name) changes.name = form.name;
    if (String(form.age) !== String(initial.age)) changes.age = form.age === '' ? null : Number(form.age);
    if (form.gender !== initial.gender) changes.gender = form.gender || null;
    if (form.isPrimary !== initial.isPrimary) changes.isPrimary = form.isPrimary;
    if (form.isActive !== initial.isActive) changes.isActive = form.isActive;

    if (Object.keys(changes).length === 0) {
      onClose();
      return;
    }

    try {
      setSaving(true);
      setError(null);
      await adminApi.updateReader(reader.id, changes);
      onSaved(`Updated reader "${form.name}"`);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not update this reader.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminModal
      title={`Edit ${reader.name}`}
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="admin-modal-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="submit" form="reader-edit-form" className="admin-modal-btn is-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </>
      )}
    >
      {error && <div className="admin-modal-error">{error}</div>}

      <form id="reader-edit-form" onSubmit={handleSubmit}>
        <AdminField label="Name">
          <input type="text" value={form.name} onChange={set('name')} maxLength={100} required />
        </AdminField>
        <AdminField label="Age">
          <input type="number" min="0" max="120" value={form.age} onChange={set('age')} />
        </AdminField>
        <AdminField label="Gender">
          <input type="text" value={form.gender} onChange={set('gender')} />
        </AdminField>
        <AdminCheck
          label="Primary reader"
          hint="A user has one primary reader; promoting this one demotes the others."
          checked={form.isPrimary}
          onChange={set('isPrimary')}
        />
        <AdminCheck
          label="Active"
          hint="Inactive readers stay in the data but drop out of family reader lists."
          checked={form.isActive}
          onChange={set('isActive')}
        />
      </form>
    </AdminModal>
  );
};

const ReaderReassignModal = ({ reader, onClose, onSaved }) => {
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      setSaving(true);
      setError(null);

      const lookup = await adminApi.getUsers({ search: email, limit: 5 });
      const owner = (lookup.data.users || []).find(
        (candidate) => candidate.email.toLowerCase() === email.trim().toLowerCase()
      );
      if (!owner) {
        setError(`No user found with the email ${email}.`);
        return;
      }

      const result = await adminApi.reassignReader(reader.id, owner.id);
      onSaved(`Moved ${reader.name} to ${owner.email}, carrying ${result.data.reportsMoved} report(s)`);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not reassign this reader.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminModal
      title={`Move ${reader.name} to another user`}
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="admin-modal-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="submit" form="reader-reassign-form" className="admin-modal-btn is-primary" disabled={saving}>
            {saving ? 'Moving…' : 'Move reader'}
          </button>
        </>
      )}
    >
      {error && <div className="admin-modal-error">{error}</div>}

      <p className="admin-modal-hint">
        The reader keeps their whole reading history, and their reports move to the new owner with
        them. Family links are cleared, because family membership follows the owner — add the reader
        to the new owner&apos;s family afterwards if they need to be in one.
      </p>

      <form id="reader-reassign-form" onSubmit={handleSubmit}>
        <AdminField label="New owner email">
          <input type="email" value={email} onChange={(event) => setEmail(event.target.value)} required />
        </AdminField>
      </form>
    </AdminModal>
  );
};

const ReaderDeleteModal = ({ reader, onClose, onDeleted }) => {
  const [impact, setImpact] = useState(null);
  const [impactError, setImpactError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // A failed preview used to leave the spinner up forever while Delete stayed
  // enabled. Deleting blind is exactly what the preview exists to prevent.
  useEffect(() => {
    adminApi.getReaderImpact(reader.id)
      .then((response) => setImpact(response.data.impact))
      .catch((err) => setImpactError(apiErrorMessage(err, 'Could not check what deleting this reader would remove.')));
  }, [reader.id]);

  const handleConfirm = async () => {
    try {
      setBusy(true);
      setError(null);
      await adminApi.deleteReader(reader.id);
      onDeleted(`Deleted reader "${reader.name}"`);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not delete this reader.'));
      setBusy(false);
    }
  };

  return (
    <ConfirmModal
      title={`Delete ${reader.name}`}
      confirmLabel="Delete reader"
      confirmPhrase={reader.name}
      busy={busy}
      disabled={!impact}
      error={error || impactError}
      onCancel={onClose}
      onConfirm={handleConfirm}
    >
      <p className="admin-modal-hint">
        This removes the reader and everything derived from them. It cannot be undone.
      </p>
      {impact ? (
        <ul className="admin-modal-list">
          <li><strong>{impact.reports}</strong> reading report(s)</li>
          <li><strong>{impact.rawReports}</strong> bulk chapter report(s)</li>
          <li><strong>{impact.completions}</strong> Bible completion record(s)</li>
          <li><strong>{impact.badges}</strong> earned badge(s)</li>
          <li><strong>{impact.planProgress}</strong> reading plan progress row(s)</li>
          <li><strong>{impact.transactions}</strong> points history row(s)</li>
          <li><strong>{impact.claims}</strong> claim(s)</li>
          <li>Removed from <strong>{impact.families}</strong> family group(s)</li>
        </ul>
      ) : impactError ? null : (
        <LoadingSpinner size="small" message="Checking what this would remove…" />
      )}
      <p className="admin-modal-hint">
        Deactivating the reader instead keeps all of this and can be undone.
      </p>
    </ConfirmModal>
  );
};

const AdminReadersPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [readers, setReaders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({});
  const [notice, setNotice] = useState(null);
  const [busyReaderId, setBusyReaderId] = useState(null);

  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [currentPage, setCurrentPage] = useState(1);
  const userId = searchParams.get('userId');
  const debouncedSearch = useDebouncedValue(search);
  const beginRequest = useLatestRequest();
  const firstLoad = useFirstLoad(loading);

  const [editing, setEditing] = useState(null);
  const [reassigning, setReassigning] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const loadReaders = useCallback(async () => {
    const request = beginRequest();
    try {
      setLoading(true);
      const response = await adminApi.getReaders({
        page: currentPage,
        limit: 20,
        search: debouncedSearch,
        sortBy,
        sortOrder,
        userId
      });
      if (!request.isCurrent()) return;
      setReaders(response.data.readers || []);
      setPagination(response.data.pagination || {});
      setError(null);
    } catch (err) {
      if (!request.isCurrent()) return;
      setError(apiErrorMessage(err, 'Failed to load readers.'));
    } finally {
      if (request.isCurrent()) setLoading(false);
    }
  }, [beginRequest, currentPage, debouncedSearch, sortBy, sortOrder, userId]);

  const handleSortChange = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(field);
      setSortOrder(field === 'name' ? 'ASC' : 'DESC');
    }
    setCurrentPage(1);
  };

  useEffect(() => { loadReaders(); }, [loadReaders]);

  const closeModals = () => {
    setEditing(null);
    setReassigning(null);
    setDeleting(null);
  };

  const handleSaved = (message) => {
    closeModals();
    setNotice(message);
    loadReaders();
  };

  const selection = useRowSelection(`${currentPage}|${debouncedSearch}|${sortBy}|${sortOrder}|${userId || ''}`);
  const bulk = useBulkAction((_result, text) => {
    selection.clear();
    setNotice(text);
    loadReaders();
  });

  const askReaderBulk = (rows, spec) => {
    const wanted = rows.filter(spec.keep);
    if (wanted.length === 0) {
      setNotice(spec.empty);
      return;
    }
    bulk.ask({
      title: spec.title,
      confirmLabel: spec.confirmLabel,
      danger: spec.danger,
      body: <p className="admin-modal-hint">{spec.body} This applies to {wanted.length} reader{wanted.length === 1 ? '' : 's'}.</p>,
      run: () => runBulk(wanted, spec.run),
      summarize: (result) => summarizeBulk(spec.past, 'reader', result)
    });
  };

  const handleResync = async (reader) => {
    try {
      setBusyReaderId(reader.id);
      setError(null);
      const result = await adminApi.resyncReader(reader.id);
      const { chaptersRead, completedCycles } = result.data.result || {};
      setNotice(`Resynced ${reader.name}: ${chaptersRead ?? '?'} chapters read, ${completedCycles ?? 0} completed cycle(s)`);
      loadReaders();
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not resync this reader.'));
    } finally {
      setBusyReaderId(null);
    }
  };

  if (firstLoad) {
    return (
      <div className="admin-loading">
        <LoadingSpinner size="large" message="Loading readers..." />
      </div>
    );
  }

  return (
    <div className="admin-readers-page">
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Readers</h1>
          <p className="admin-page-description">
            Correct reader details, move a reader to another account, and re-derive their progress
          </p>
        </div>
      </div>

      {userId && (
        <div className="reader-filter-banner">
          <span>
            Showing readers for <strong>{readers[0]?.user?.email || 'one account'}</strong> only.
          </span>
          <button onClick={() => { setSearchParams({}); setCurrentPage(1); }}>Show all readers</button>
        </div>
      )}

      {notice && (
        <div className="reader-success-banner">
          <span>{notice}</span>
          <button onClick={() => setNotice(null)}>&times;</button>
        </div>
      )}

      <div className="admin-card">
        <div className="readers-controls">
          <div className="search-box">
            <IoSearchOutline className="search-icon" />
            <input
              type="text"
              placeholder="Search readers by name..."
              value={search}
              onChange={(event) => { setSearch(event.target.value); setCurrentPage(1); }}
              className="search-input"
            />
          </div>
        </div>
      </div>

      <AdminDataTable
        title={`Readers (${pagination.totalReaders || 0})`}
        rows={readers}
        getRowLabel={(reader) => reader.name}
        selection={selection}
        loading={loading}
        error={error}
        onRetry={loadReaders}
        sort={{ key: sortBy, order: sortOrder, onChange: handleSortChange }}
        emptyTitle="No readers found"
        emptyMessage="No readers match your search criteria."
        pagination={{
          page: currentPage,
          totalPages: pagination.totalPages,
          hasPrev: pagination.hasPrevPage,
          hasNext: pagination.hasNextPage,
          onPageChange: setCurrentPage
        }}
        bulkActions={[
          {
            label: 'Deactivate',
            onClick: (rows) => askReaderBulk(rows, {
              title: 'Deactivate selected readers',
              confirmLabel: 'Deactivate',
              past: 'Deactivated',
              keep: (row) => row.isActive !== false,
              empty: 'None of the selected readers are active.',
              run: (row) => adminApi.updateReader(row.id, { isActive: false }),
              body: 'Inactive readers stay in the data and drop out of family reader lists.'
            })
          },
          {
            label: 'Reactivate',
            danger: false,
            onClick: (rows) => askReaderBulk(rows, {
              title: 'Reactivate selected readers',
              confirmLabel: 'Reactivate',
              past: 'Reactivated',
              danger: false,
              keep: (row) => row.isActive === false,
              empty: 'None of the selected readers are inactive.',
              run: (row) => adminApi.updateReader(row.id, { isActive: true }),
              body: 'These readers will show up in family reader lists again.'
            })
          },
          {
            label: 'Resync',
            danger: false,
            onClick: (rows) => askReaderBulk(rows, {
              title: 'Resync selected readers',
              confirmLabel: 'Resync',
              past: 'Resynced',
              danger: false,
              keep: () => true,
              empty: 'Select at least one reader.',
              run: (row) => adminApi.resyncReader(row.id),
              body: 'Bible completions and badges are re-derived from each reader’s reports. Badges already earned are kept.'
            })
          }
        ]}
        columns={[
          {
            key: 'name',
            header: 'Name',
            sortKey: 'name',
            render: (reader) => (
              <span className="admin-cell-strong">
                {reader.name}
                <span className="admin-cell-sub">
                  {reader.isPrimary && <AdminPill tone="info">Primary</AdminPill>}
                  {reader.isActive === false && <AdminPill tone="danger">Inactive</AdminPill>}
                </span>
              </span>
            )
          },
          { key: 'owner', header: 'Owner', render: (reader) => reader.user?.email || 'unknown' },
          { key: 'reports', header: 'Reports', render: (reader) => reader.reportCount },
          { key: 'age', header: 'Age', sortKey: 'age', render: (reader) => reader.age ?? '—' },
          { key: 'church', header: 'Church', render: (reader) => reader.church?.name || '—' },
          {
            key: 'created',
            header: 'Added',
            sortKey: 'createdAt',
            render: (reader) => new Date(reader.createdAt).toLocaleDateString()
          }
        ]}
        renderActions={(reader) => (
          <>
            <button type="button" onClick={() => setEditing(reader)} title="Edit reader">
              <IoCreateOutline /> Edit
            </button>
            <button type="button" onClick={() => setReassigning(reader)} title="Move to another user">
              <IoSwapHorizontalOutline /> Move
            </button>
            <button
              type="button"
              onClick={() => handleResync(reader)}
              disabled={busyReaderId === reader.id}
              title="Re-derive completions and badges from this reader's reports"
            >
              <IoRefreshOutline /> {busyReaderId === reader.id ? 'Resyncing…' : 'Resync'}
            </button>
            <button type="button" className="is-danger" onClick={() => setDeleting(reader)} title="Delete reader">
              <IoTrashOutline /> Delete
            </button>
          </>
        )}
      />
      <BulkActionHost bulk={bulk} />

      {editing && <ReaderEditModal reader={editing} onClose={closeModals} onSaved={handleSaved} />}
      {reassigning && <ReaderReassignModal reader={reassigning} onClose={closeModals} onSaved={handleSaved} />}
      {deleting && <ReaderDeleteModal reader={deleting} onClose={closeModals} onDeleted={handleSaved} />}
    </div>
  );
};

export default AdminReadersPage;
